import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

interface Brand {
  id: string;
  brand_name: string;
  brand_slug: string;
  logo_url: string;
  is_active: boolean;
  sort_order: number;
}

interface Category {
  id: string;
  category_name: string;
  category_slug: string;
  is_active: boolean;
  show_on_nav: boolean;
  sort_order: number;
}

interface SystemSettings {
  site_logo_url: string;
  default_language: string;
  enabled_languages: string[];
}

export default function TopNavigation() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cartCount, setCartCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('EN');
  const [enabledLanguages, setEnabledLanguages] = useState<string[]>(['EN', 'RU', 'ES', 'AR', 'PT']);
  const [logoUrl, setLogoUrl] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState<string | null>(null);
  
  const brandsDropdownRef = useRef<HTMLDivElement>(null);
  const productsDropdownRef = useRef<HTMLDivElement>(null);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (supabase) {
      loadSystemSettings();
      loadBrands();
      loadCategories();
      checkAuthStatus();
      loadCartCount();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        brandsDropdownRef.current &&
        !brandsDropdownRef.current.contains(event.target as Node) &&
        productsDropdownRef.current &&
        !productsDropdownRef.current.contains(event.target as Node) &&
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSystemSettings = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('site_logo_url, default_language, enabled_languages')
        .single();

      if (error) throw error;
      if (data) {
        setLogoUrl(data.site_logo_url || '');
        setCurrentLanguage(data.default_language || 'EN');
        setEnabledLanguages(data.enabled_languages || ['EN', 'RU', 'ES', 'AR', 'PT']);
      }
    } catch (error) {
      console.error('Error loading system settings:', error);
    }
  };

  const loadBrands = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setBrands(data || []);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const loadCategories = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .eq('show_on_nav', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const checkAuthStatus = async () => {
    if (!supabase) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setIsLoggedIn(!!user);
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  };

  const loadCartCount = async () => {
    if (!supabase) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCartCount(0);
        return;
      }

      const { data: cart } = await supabase
        .from('carts')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (cart) {
        const { data: items } = await supabase
          .from('cart_items')
          .select('cartons')
          .eq('cart_id', cart.id);

        const total = items?.reduce((sum, item) => sum + (item.cartons || 0), 0) || 0;
        setCartCount(total);
      }
    } catch (error) {
      console.error('Error loading cart count:', error);
    }
  };

  const toggleDropdown = (menu: string) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  const toggleMobileMenu = (menu: string) => {
    setMobileExpandedMenu(mobileExpandedMenu === menu ? null : menu);
  };

  const handleLanguageChange = (lang: string) => {
    setCurrentLanguage(lang);
    setOpenDropdown(null);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileExpandedMenu(null);
  };

  const getLanguageDisplayName = (code: string) => {
    const languageNames: Record<string, string> = {
      'EN': 'English',
      'RU': 'Русский',
      'ES': 'Español',
      'AR': 'العربية',
      'PT': 'Português',
    };
    return languageNames[code.toUpperCase()] || code;
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* LEFT AREA - LOGO */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-auto" style={{ width: '200px' }} />
                ) : (
                  <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
                    logo
                  </span>
                )}
              </Link>
            </div>

            {/* CENTER AREA - DESKTOP MENU */}
            <div className="hidden md:flex items-center space-x-8">
              {/* (1) Home */}
              <Link
                to="/"
                className="text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Home
              </Link>

              {/* (2) Brands Dropdown */}
              <div className="relative" ref={brandsDropdownRef}>
                <button
                  onClick={() => toggleDropdown('brands')}
                  className="flex items-center text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Brands
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform ${openDropdown === 'brands' ? 'rotate-180' : ''}`}></i>
                </button>
                {openDropdown === 'brands' && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                    <Link
                      to="/brands"
                      onClick={() => setOpenDropdown(null)}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      All brands
                    </Link>
                    {brands.map((brand) => (
                      <Link
                        key={brand.id}
                        to={`/brand/${brand.brand_slug}`}
                        onClick={() => setOpenDropdown(null)}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        {brand.brand_name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* (3) Products Dropdown */}
              <div className="relative" ref={productsDropdownRef}>
                <button
                  onClick={() => toggleDropdown('products')}
                  className="flex items-center text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Products
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform ${openDropdown === 'products' ? 'rotate-180' : ''}`}></i>
                </button>
                {openDropdown === 'products' && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                    <Link
                      to="/products"
                      onClick={() => setOpenDropdown(null)}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      All products
                    </Link>
                    {categories.map((category) => (
                      <Link
                        key={category.id}
                        to={`/category/${category.category_slug}`}
                        onClick={() => setOpenDropdown(null)}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        {category.category_name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* (4) Special Offers */}
              <Link
                to="/specials"
                className="text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Special offers
              </Link>

              {/* (5) ALL */}
              <Link
                to="/products"
                className="text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                ALL
              </Link>
            </div>

            {/* RIGHT AREA - LANGUAGE, USER, CART */}
            <div className="hidden md:flex items-center space-x-6">
              {/* Language Switcher */}
              <div className="relative" ref={languageDropdownRef}>
                <button
                  onClick={() => toggleDropdown('language')}
                  className="flex items-center text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-global-line mr-1"></i>
                  {currentLanguage}
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform ${openDropdown === 'language' ? 'rotate-180' : ''}`}></i>
                </button>
                {openDropdown === 'language' && (
                  <div className="absolute top-full right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                    {enabledLanguages.map((lang) => (
                      <button
                        key={lang}
                        onClick={() => handleLanguageChange(lang)}
                        className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        {getLanguageDisplayName(lang)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* User Entry */}
              {isLoggedIn ? (
                <Link
                  to="/account"
                  className="flex items-center text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-user-line mr-1"></i>
                  My Account
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-login-box-line mr-1"></i>
                  Login / Register
                </Link>
              )}

              {/* Cart */}
              <Link
                to="/cart"
                className="text-gray-700 hover:text-teal-600 transition-colors flex items-center"
              >
                <i className="ri-shopping-cart-line text-xl"></i>
              </Link>

              <Link
                to="/account/orders"
                className="text-gray-700 hover:text-teal-600 transition-colors flex items-center"
              >
                <i className="ri-file-list-3-line text-xl"></i>
              </Link>
            </div>

            {/* MOBILE - LANGUAGE + HAMBURGER */}
            <div className="flex md:hidden items-center space-x-4">
              {/* Language Button */}
              <button
                onClick={() => toggleDropdown('language')}
                className="flex items-center text-gray-700 hover:text-gray-900 cursor-pointer"
              >
                <i className="ri-global-line text-xl"></i>
              </button>
              {openDropdown === 'language' && (
                <div className="absolute top-16 right-16 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
                  {enabledLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => handleLanguageChange(lang)}
                      className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      {getLanguageDisplayName(lang)}
                    </button>
                  ))}
                </div>
              )}

              {/* Hamburger Menu */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-700 hover:text-gray-900 cursor-pointer"
              >
                <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* MOBILE MENU DRAWER */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto">
          <div className="px-4 py-6 space-y-4">
            {/* (1) Home */}
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="block text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer"
            >
              Home
            </Link>

            {/* (2) Brands */}
            <div>
              <button
                onClick={() => toggleMobileMenu('brands')}
                className="flex items-center justify-between w-full text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer"
              >
                Brands
                <i className={`ri-arrow-down-s-line transition-transform ${mobileExpandedMenu === 'brands' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileExpandedMenu === 'brands' && (
                <div className="pl-4 mt-2 space-y-2">
                  <Link
                    to="/brands"
                    onClick={closeMobileMenu}
                    className="block text-gray-600 hover:text-gray-900 py-2 cursor-pointer"
                  >
                    All brands
                  </Link>
                  {brands.map((brand) => (
                    <Link
                      key={brand.id}
                      to={`/brand/${brand.brand_slug}`}
                      onClick={closeMobileMenu}
                      className="block text-gray-600 hover:text-gray-900 py-2 cursor-pointer"
                    >
                      {brand.brand_name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* (3) Products */}
            <div>
              <button
                onClick={() => toggleMobileMenu('products')}
                className="flex items-center justify-between w-full text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer"
              >
                Products
                <i className={`ri-arrow-down-s-line transition-transform ${mobileExpandedMenu === 'products' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileExpandedMenu === 'products' && (
                <div className="pl-4 mt-2 space-y-2">
                  <Link
                    to="/products"
                    onClick={closeMobileMenu}
                    className="block text-gray-600 hover:text-gray-900 py-2 cursor-pointer"
                  >
                    All products
                  </Link>
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.category_slug}`}
                      onClick={closeMobileMenu}
                      className="block text-gray-600 hover:text-gray-900 py-2 cursor-pointer"
                    >
                      {category.category_name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* (4) Special Offers */}
            <Link
              to="/specials"
              onClick={closeMobileMenu}
              className="block text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer"
            >
              Special offers
            </Link>

            {/* (5) ALL */}
            <Link
              to="/products"
              onClick={closeMobileMenu}
              className="block text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer"
            >
              ALL
            </Link>

            <div className="border-t border-gray-200 pt-4 mt-4">
              {/* (6) My Account / Login */}
              {isLoggedIn ? (
                <Link
                  to="/account"
                  onClick={closeMobileMenu}
                  className="flex items-center text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer"
                >
                  <i className="ri-user-line mr-2"></i>
                  My Account
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="flex items-center text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer"
                >
                  <i className="ri-login-box-line mr-2"></i>
                  Login / Register
                </Link>
              )}

              {/* (7) Cart */}
              <Link
                to="/cart"
                onClick={closeMobileMenu}
                className="flex items-center text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer"
              >
                <i className="ri-shopping-cart-line mr-2"></i>
                Cart ({cartCount})
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
